//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { FormControlLabel, makeStyles, Radio } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
// import { Elements } from '@stripe/react-stripe-js';
// import { loadStripe } from '@stripe/stripe-js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getTierTitle } from '../../common/lovHelper';
import {
    ADD_REMOVE,
    BUY_NOW,
    CANCEL_ALL,
    CHANGE_TO_MONTHLY,
    CHANGE_TO_YEARLY,
    CONFIRM_DIALOG_MESSAGE_CANCEL_SELECTION,
    CONFIRM_DIALOG_TITLE_CANCEL_SELECTION,
    DASHBOARD_MY_CALENDAR,
    MESSAGE_ENGAGEMENT_INFO,
    MESSAGE_TITLE_ENGAGEMENT_INFO,
} from '../../common/constants';
import { getUniqueKey } from '../../common/arrayHelper';
import { getFormattedDate, getUserDateFormat } from '../../common/dateHelper';
// import { getEngagementRemainingCountByEmployerTiersData, getEngagementTotalListByJobTitle, getTotalEngagementMaxByTiersData } from '../../common/employerHelper';
// import { getCurrentBillingPeriodEngagements } from '../../common/services/empDashboardServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { activateSubscription, downgradeAdditionalSeatSubscription, downgradeTierSubscription } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import OrderDialog from './OrderDialog';
import PaymentDialog from './PaymentDialog';
// import DowngradeDialog from './DowngradeDialog';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';
import ConfirmDowngradeDialog from './ConfirmDowngradeDialog';
import CancelAdditionalSeatDialog from './CancelAdditionalSeatDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
// import _map from 'lodash/map';
// import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            [theme.breakpoints.down('md')]: {
                paddingBottom: 45,
            },
        },
        cardContainer: {
            [theme.breakpoints.up('md')]: {
                display: 'flex',
                justifyContent: 'space-between',
            },
        },
        card: {
            padding: 16,
            // '&:not(:last-child)': { marginRight: 16 },
            // width: '100%',
            borderRadius: 5,
            border: '2px solid rgba(29,138,139,1)',
            [theme.breakpoints.down('md')]: { marginBottom: 16, marginRight: 4 },
            [theme.breakpoints.up('md')]: { '&:not(:last-child)': { marginRight: 16 }, width: '100%', marginBottom: 16 },
        },
        activeRadioLabel: { color: 'rgb(29,138,139)', fontWeight: 'bold' },
        radioLabel: { color: 'rgba(17,30,20,0.4)', fontWeight: 'bold' },
        cardTitle: { fontWeight: 'bold', marginTop: 10 },
        cardTier: { fontWeight: 'bold' },
        cardPrice: { fontWeight: 'bold', fontSize: 21, marginTop: 10 },
        subscribedCardPrice: { fontWeight: 'bold', fontSize: 21, marginTop: 10, color: 'white' },
        cardPriceSave: { padding: '4px 8px', background: 'rgba(252,130,31,0.2)', width: 'fit-content', borderRadius: 50, marginTop: 20 },

        subscribedCard: {
            padding: 16,
            // '&:not(:last-child)': { marginRight: 16 },
            // width: '100%',
            borderRadius: 5,
            background: 'rgba(29,138,139,1)',
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('md')]: { marginBottom: 16, marginRight: 4 },
            [theme.breakpoints.up('md')]: { '&:not(:last-child)': { marginRight: 16 }, width: '100%', marginBottom: 16 },
        },
        subscribedCardTierTitle: { color: 'white', textTransform: 'uppercase' },
        subscribedCardStatusText: { color: 'white', fontWeight: 'bold', marginTop: 30, [theme.breakpoints.down('md')]: { marginTop: 16 } },
        subscribedCardTierText: { color: 'white', fontWeight: 'bold', [theme.breakpoints.down('md')]: { paddingBottom: 80 } },
        subscribedCardTierUpdateButton: { color: 'white', marginTop: 'auto', textTransform: 'uppercase', cursor: 'pointer' },
        subscribedCardTierCancelButton: { color: 'white', marginTop: 'auto', textTransform: 'uppercase', cursor: 'pointer' },

        tierTitle: { fontWeight: 'bold', margin: '20px 0' },

        tierDetailsContainer: { background: '#F0F2F6', borderRadius: 4, padding: 20 },
        tierDetail: { display: 'flex', marginBottom: 10 },
        tierDetailText: { marginLeft: 10, fontWeight: 'bold' },

        backButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { marginRight: 0, marginBottom: 16 } },
        cancelButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { marginRight: 0, marginBottom: 16 } },
        saveButton: { [theme.breakpoints.down('sm')]: { marginBottom: 16 } },
        buttonBox: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '30px 0 0 0',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        },

        hidden: { display: 'none' },

        subscribedTierContainer: { textAlign: 'right' },
        // subscribedTier3Container: { margin: '20px 0', textAlign: 'right' },
        engageText: { color: 'blue', textDecoration: 'underline', cursor: 'pointer' },

        job: { background: 'rgba(29,138,139,0.09)', padding: 10, marginTop: 10, borderRadius: 4, '&:nth-of-type(even)': { background: '#FFF8E5' } },
        jobTitle: { fontWeight: 'bold' },

        cancelText: { color: 'red' },
        subscribedCardPriceSpan: { color: 'white' },
        additionalSeatsText: { fontWeight: 'bold' },
        cancelAdditionalSeatsButton: { color: 'rgb(252,130,31,1)', fontWeight: 'bold' },
        additionalSeatsButton: { color: 'rgb(252,130,31,1)', fontWeight: 'bold' },
        additionalSeatsContainer: {
            background: '#FFF8E5',
            padding: 10,
            marginTop: 10,
            marginBottom: 20,
            borderRadius: 4,
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
        },
        seatsToRemove: { background: 'rgba(29,138,139,0.09)', padding: 10, marginTop: 20, fontWeight: 'bold', borderRadius: 4, textAlign: 'left' },

        vacantSeatsText: { color: 'rgb(29,138,139)', fontWeight: 'bold' },
        additionalSeatsAddedText: { color: 'rgb(29,138,139)', fontWeight: 'bold' },

        activeJobEngagementListTitleText: { marginTop: 24 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        pendingAccountSetup,
        selectedTier,
        setSelectedTier,
        cancelModalFlag,
        setCancelModalFlag,
        orderMode,
        setOrderMode,
        purchaseMode,
        setPurchaseMode,
        handlePaymentDialogSuccessPurchase,
        tiers,
        subscribedTier,
        subscribedMonthly,
        cancelSubscriptionModalFlag,
        setCancelSubscriptionModalFlag,
        cancelAdditionalSeatModalOpen,
        setCancelAdditionalSeatModalOpen,
        // downgradeModalFlag,
        // setDowngradeModalFlag,
        engageModal,
        setEngageModal,
        handleClickSaveButton,
        handleCancelButton,
        handleClickBackButton,
        confirmDowngradeModalOpen,
        // setDowngradeConfirmModalOpen,
        currentEmployer,
        // isSubscribedToTier3,
        trialAvailable,
        currentTier,
        additionalSeats,
        handleOrderDialogProceedToPayment,
        handleOrderDialogSubmitDowngradeOrder,
        monthly,
        // isAdditionalSeatOrder,
        invoices,
        handleClickUpdatePlan,
        handleConfirmDowngrade,
        handleClickCancelAdditionalSeatsButton,
        downgradeLoading,
    } = params;
    const { hidden: hiddenCls, buttonBox: buttonBoxCls, subscribedTierContainer: subscribedTierContainerCls, job: jobCls, seatsToRemove: seatsToRemoveCls } = classes;

    const isDirty = !!selectedTier;
    const isDowngrade = subscribedTier && subscribedTier > selectedTier;
    const orderDialogOpen = !!orderMode;
    const paymentDialogOpen = !!purchaseMode;

    // const saveButtonLabel = `${
    //     pendingAccountSetup && !trialAvailable
    //         ? `Purchase Tier ${selectedTier}`
    //         : pendingAccountSetup && trialAvailable
    //         ? `Tier ${selectedTier} Free Trial`
    //         : isDowngrade
    //         ? `Downgrade to Tier ${selectedTier}`
    //         : subscribedTier
    //         ? `Upgrade to Tier ${selectedTier}`
    //         : `Purchase Tier ${selectedTier}`
    // } `;
    const saveButtonLabel = `${
        pendingAccountSetup && !trialAvailable
            ? `Continue with Tier ${selectedTier}`
            : pendingAccountSetup && trialAvailable
            ? `Tier ${selectedTier} Free Trial`
            : isDowngrade
            ? `Downgrade to Tier ${selectedTier}`
            : subscribedTier
            ? `Upgrade to Tier ${selectedTier}`
            : `Continue with Tier ${selectedTier}`
    } `;
    const updatePlanButtonLabel = subscribedMonthly ? CHANGE_TO_YEARLY : CHANGE_TO_MONTHLY;

    const stripeAdditionalSeatSubId = currentEmployer?.stripeAdditionalSeatSubId;
    const members = _get(currentEmployer, 'members', []);
    const invited = _get(currentEmployer, 'invited', []);
    const vacantSeats = tiers?.find(el => el.tier === subscribedTier)?.seatMax + (subscribedTier === 3 ? additionalSeats : 0) - members.length - invited.length;

    const showVacantSeats = subscribedTier !== 1 && vacantSeats >= 0;

    const additionalSeatsButtonLabel = stripeAdditionalSeatSubId ? ADD_REMOVE : BUY_NOW;
    const cancelAdditionalSeatsButtonIsHidden = !stripeAdditionalSeatSubId;

    const defns = {
        cardTitle: { variant: 'body1' },
        cardTier: { variant: 'body2' },
        cardPrice: { variant: 'body2', color: 'secondary' },
        subscribedCardPrice: { variant: 'body2', color: 'secondary' },
        cardPriceSpan: { variant: 'body2', component: 'span', color: 'secondary' },
        subscribedCardPriceSpan: { variant: 'body2', component: 'span', color: 'secondary' },
        cardPriceSave: { variant: 'body2', color: 'primary' },

        subscribedCardTierTitle: { variant: 'caption', value: 'Current Subscription' },
        subscribedCardStatusText: { variant: 'body1', value: `${getTierTitle(subscribedTier)?.slice(0, -9)}` },
        subscribedCardTierText: { variant: 'body2', value: `Tier ${subscribedTier}` },
        subscribedCardTierUpdateButton: { variant: 'caption', value: updatePlanButtonLabel, onClick: handleClickUpdatePlan },
        subscribedCardTierCancelButton: { variant: 'caption', value: 'Cancel Plan', onClick: () => setCancelSubscriptionModalFlag(true) },

        tierTitle: { variant: 'body2' },
        tickIcon: { color: 'secondary' },
        tierDetailText: { variant: 'body2' },

        buttonBox: { className: isDirty || pendingAccountSetup ? buttonBoxCls : hiddenCls },

        backButton: { label: 'Back', onClick: handleClickBackButton },
        cancelButton: { label: 'Cancel', disabled: !isDirty, onClick: () => setCancelModalFlag(true) },
        saveButton: { label: saveButtonLabel, isHidden: !selectedTier, disabled: !isDirty, color: 'primary', onClick: handleClickSaveButton.bind(null, isDowngrade) },

        orderDialog: {
            open: orderDialogOpen,
            variant: orderMode, //isSubscribedToTier3 ? 'additional-seat' : 'tier',
            onClose: () => setOrderMode(null),
            selectedTier: orderMode === 'additional-seat' || orderMode === 'interval' ? currentTier : tiers.find(el => el.tier === selectedTier),
            setIsDirtyFlagNull: () => setSelectedTier(0),
            shouldUpdate: Boolean(subscribedTier),
            onProceedToPayment: handleOrderDialogProceedToPayment,
            onSubmitDowngradeOrder: handleOrderDialogSubmitDowngradeOrder,
            onCancelAdditionSeatsButtonClick: handleClickCancelAdditionalSeatsButton,
        },
        paymentModal: {
            open: paymentDialogOpen,
            variant: purchaseMode,
            onClose: () => setPurchaseMode(null),
            selectedTier: purchaseMode === 'additional-seat' || purchaseMode === 'interval' ? currentTier : tiers.find(el => el.tier === selectedTier),
            setIsDirtyFlagNull: () => setSelectedTier(0),
            shouldUpdate: Boolean(subscribedTier),
            onSuccessPurchase: handlePaymentDialogSuccessPurchase,
            // isSubscribedTier3: isSubscribedToTier3,
            monthly,
            invoices,
        },
        cancelSubscriptionModal: {
            subscribedTier,
            open: cancelSubscriptionModalFlag,
            onClose: () => {
                setCancelSubscriptionModalFlag(false);
            },
            setIsDirtyFlagNull: () => setSelectedTier(0),
        },
        cancelModal: {
            title: CONFIRM_DIALOG_TITLE_CANCEL_SELECTION,
            open: cancelModalFlag,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL_SELECTION,
            onClose: () => setCancelModalFlag(false),
            onConfirm: () => {
                setSelectedTier(0);
                setCancelModalFlag(false);
            },
        },
        cancelAdditionalSeatModal: {
            additionalSeats,
            open: cancelAdditionalSeatModalOpen,
            onClose: () => setCancelAdditionalSeatModalOpen(false),
        },
        engagementInfoModal: {
            title: MESSAGE_TITLE_ENGAGEMENT_INFO,
            open: engageModal,
            message: MESSAGE_ENGAGEMENT_INFO,
            onClose: () => setEngageModal(false),
        },
        confirmDowngradeModal: {
            subscribedTier,
            selectedTier,
            onClose: handleCancelButton,
            onConfirm: handleConfirmDowngrade,
            // onConfirm: () => setDowngradeModalFlag(true),
            open: confirmDowngradeModalOpen,
            yesButtonLoading: downgradeLoading,
        },
        // // this is the seat reduction modal
        // downgradeModal: {
        //     open: downgradeModalFlag,
        //     onClose: () => setDowngradeModalFlag(false),
        //     setIsDirtyFlagNull: () => {
        //         setDowngradeConfirmModalOpen(false);
        //         setSelectedTier(0);
        //     },
        //     selectedTier,
        //     tiers,
        //     hideSeatReductionModal: downgradeModalFlag && (selectedTier === 1 || (selectedTier === 2 && members.length <= 3)),
        // },

        subscribedTierContainer: { className: !!subscribedTier ? subscribedTierContainerCls : hiddenCls },

        engageText: { onClick: () => setEngageModal(true) },

        job: { className: subscribedTier !== 0 && subscribedTier !== 3 ? jobCls : hiddenCls },
        additionalSeatsContainer: { variant: 'body2', onClick: () => setOrderMode('additional-seat') },
        additionalSeatsText: { variant: 'body2', value: 'Need additional seats?' },
        cancelAdditionalSeatsButton: { variant: 'body1', value: CANCEL_ALL, isHidden: cancelAdditionalSeatsButtonIsHidden, onClick: handleClickCancelAdditionalSeatsButton },
        additionalSeatsButton: { variant: 'body1', value: additionalSeatsButtonLabel },
        seatsToRemove: { variant: 'body2', className: showVacantSeats ? seatsToRemoveCls : hiddenCls },
        vacantSeatsText: { variant: 'body2', value: `${vacantSeats} vacant`, component: 'span' },
        additionalSeatsAddedText: { variant: 'body2', value: `${additionalSeats} additional`, component: 'span' },

        activeJobEngagementListTitleText: { variant: 'h6' },
    };

    // inject classes
    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// // Make sure to call `loadStripe` outside of a component’s render to avoid
// // recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(STRIPE_API_PUBLISHABLE_KEY);

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Subscription = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const { userId, currentEmployer, sys, employerId, pendingAccountSetup, setThankYouForPurchaseDialogOpen } = appContext;
    const layoutContext = useContext(LayoutContext);
    const { setCurrentDashboard, accountSetupGoToGeneral, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const userDateFormat = getUserDateFormat(appContext);

    const tiers = _get(sys, 'tiers', []);
    const stripeSubId = _get(currentEmployer, 'stripeSubId');
    const trialAvailable = _get(currentEmployer, 'trialAvailable');
    const stripeAdditionalSeatSubId = _get(currentEmployer, 'stripeAdditionalSeatSubId');
    const subscribedTier = _get(currentEmployer, 'tier', 0);
    const subscribedMonthly = _get(currentEmployer, 'billMonthly');
    // const billCycleStartDt = _get(currentEmployer, 'billCycleStartDt');
    const tierNew = _get(currentEmployer, 'tierNew', 0);
    const billMonthlyNew = _get(currentEmployer, 'billMonthlyNew');
    const additionalSeats = _get(currentEmployer, 'additionalSeats', 0);
    const subscribedTierDetails = tiers.find(el => el.tier === subscribedTier);
    const billCycleEndDt = currentEmployer.billCycleEndDt ? currentEmployer.billCycleEndDt : new Date();
    const unsubscribeDt = currentEmployer.unsubscribeDt?.toDate ? currentEmployer.unsubscribeDt?.toDate() : null;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { inviteCode } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [selectedTier, setSelectedTier] = useState(0);
    const [cancelModalFlag, setCancelModalFlag] = useState(false);
    const [orderMode, setOrderMode] = useState(null);
    const [purchaseMode, setPurchaseMode] = useState(null);
    const [cancelSubscriptionModalFlag, setCancelSubscriptionModalFlag] = useState(false);
    const [cancelAdditionalSeatModalOpen, setCancelAdditionalSeatModalOpen] = useState(false);
    // const [downgradeModalFlag, setDowngradeModalFlag] = useState(false);
    const [engageModal, setEngageModal] = useState(false);
    const [confirmDowngradeModalOpen, setDowngradeConfirmModalOpen] = useState(false);
    // const [currentBillPeriodEngagements, setCurrentBillPeriodEngagements] = useState([]);
    const [monthly, setMonthly] = useState();
    const [isAdditionalSeatOrder, setIsAdditionalSeatOrder] = useState();
    const [invoices, setInvoices] = useState();
    const [downgradeLoading, setDowngradeLoading] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     let unsubscribeLoadEngagementsData = null;

    //     if (!_isNil(employerId) && !_isNil(subscribedTier) && (subscribedTier === 2 || subscribedTier === 1)) {
    //         // 1657427745000 7/10 12:35am
    //         // 1657449345000 7/10 6:35am
    //         // unsubscribeLoadEngagementsData = getCurrentBillingPeriodEngagements({
    //         //     employerId,
    //         //     billCycleStartDt: new Date(1657427745000),
    //         //     setList: setCurrentBillPeriodEngagements,
    //         // });
    //         unsubscribeLoadEngagementsData = getCurrentBillingPeriodEngagements({ employerId, billCycleStartDt, setList: setCurrentBillPeriodEngagements });
    //     }

    //     // unsubscribe realtime update on unmount
    //     return () => {
    //         unsubscribeRealtimeUpdates(unsubscribeLoadEngagementsData);
    //     };
    // }, [subscribedTier, employerId, billCycleStartDt]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isSubscribedToTier3 = subscribedTier === 3;
    const currentTier = tiers.find(t => t.tier === subscribedTier);
    // console.log('isSubscribedToTier3', { isSubscribedToTier3 });
    // const currentPeriodEngagementTotalsGroupedByJobTitle = getEngagementTotalListByJobTitle(currentBillPeriodEngagements);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChange = event => setSelectedTier(Number(event.target.value));

    // const handleClickSaveButton = isDowngrade => {
    //     if (isDowngrade) {
    //         setDowngradeConfirmModalOpen(true);
    //     } else {
    //         setOrderMode('tier');
    //     }
    // };
    const handleClickSaveButton = async () => {
        //-------------------------------------------------------------------------------------------
        // Bypass Order and Payment.  Activate account.
        //-------------------------------------------------------------------------------------------
        const success = await activateSubscription({
            tier: selectedTier,
            userId,
            employerId,
            pendingAccountSetup,
            inviteCode,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });

        if (success) {
            setThankYouForPurchaseDialogOpen(true);
        }
    };

    const handleCancelButton = () => {
        setDowngradeConfirmModalOpen(false);
        setCurrentDashboard(DASHBOARD_MY_CALENDAR);
    };

    const handleClickBackButton = () => {
        accountSetupGoToGeneral({ tier: selectedTier });
    };

    // const numberWithCommas = x => {
    //     return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // };

    const handleOrderDialogProceedToPayment = ({ tier, monthly, isAdditionalSeatOrder, invoices } = {}) => {
        setInvoices(invoices);
        setMonthly(monthly);
        setIsAdditionalSeatOrder(isAdditionalSeatOrder); //TODO do we still need this?
        // setPaymentModalFlag(true);
        setPurchaseMode(orderMode);
        setOrderMode(null);
    };

    const handleOrderDialogSubmitDowngradeOrder = async ({
        isAdditionalSeatOrder,
        tierNew,
        billMonthlyNew,
        additionalSeatsNew,
        employerId,
        stripeSubId,
        stripeAdditionalSeatSubId,
        existingTier,
        existingMonthly,
        existingAdditionalSeats,
        setIsSubmittingDowngrade,
    } = {}) => {
        if (isAdditionalSeatOrder) {
            // submit additional seat downgrade order (removing seats)
            downgradeAdditionalSeatSubscription({
                additionalSeatsNew,
                employerId,
                stripeAdditionalSeatSubId,
                existingAdditionalSeats,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                callback: () => {
                    // close order dialog if successful
                    setOrderMode(null);
                },
            });
        } else {
            // submit tier downgrade order (change to monthly)
            await downgradeTierSubscription({
                tierNew,
                billMonthlyNew,
                employerId,
                stripeSubId,
                stripeAdditionalSeatSubId,
                existingTier,
                existingMonthly,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                callback: () => {
                    // close order dialog if successful
                    setOrderMode(null);
                    setIsSubmittingDowngrade && setIsSubmittingDowngrade(false);
                    setThankYouForPurchaseDialogOpen(true);
                },
            });
        }
    };

    const handleClickUpdatePlan = () => {
        setOrderMode('interval');
    };

    const handleConfirmDowngrade = async () => {
        setDowngradeLoading(true);
        await downgradeTierSubscription({
            // membersNew: value, // the function will automatically carry over members to membersNew
            tierNew: selectedTier,
            // billMonthlyNew, // comment this out so it will use existingMonthly
            employerId,
            stripeSubId,
            stripeAdditionalSeatSubId,
            existingTier: subscribedTier,
            existingMonthly: subscribedMonthly,
            setSnackbarMessage,
            setSnackbarErrorMessage,
            callback: () => {
                setDowngradeConfirmModalOpen(false);
                setSelectedTier(0);
                setDowngradeLoading(false);
                setThankYouForPurchaseDialogOpen(true);
            },
        });
    };

    const handleClickCancelAdditionalSeatsButton = evt => {
        evt.stopPropagation();
        setCancelAdditionalSeatModalOpen(true);
    };

    const handlePaymentDialogSuccessPurchase = () => {
        setPurchaseMode(false);
        setThankYouForPurchaseDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        pendingAccountSetup,
        selectedTier,
        cancelModalFlag,
        setCancelModalFlag,
        setSelectedTier,
        orderMode,
        setOrderMode,
        purchaseMode,
        setPurchaseMode,
        handlePaymentDialogSuccessPurchase,
        tiers,
        subscribedTier,
        subscribedMonthly,
        cancelSubscriptionModalFlag,
        setCancelSubscriptionModalFlag,
        cancelAdditionalSeatModalOpen,
        setCancelAdditionalSeatModalOpen,
        // downgradeModalFlag,
        // setDowngradeModalFlag,
        engageModal,
        setEngageModal,
        handleClickSaveButton,
        handleCancelButton,
        handleClickBackButton,
        confirmDowngradeModalOpen,
        setDowngradeConfirmModalOpen,
        currentEmployer,
        isSubscribedToTier3,
        trialAvailable,
        currentTier,
        additionalSeats,
        handleOrderDialogProceedToPayment,
        handleOrderDialogSubmitDowngradeOrder,
        monthly,
        isAdditionalSeatOrder,
        invoices,
        handleClickUpdatePlan,
        handleConfirmDowngrade,
        handleClickCancelAdditionalSeatsButton,
        downgradeLoading,
        setThankYouForPurchaseDialogOpen,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.cardContainer}>
                {tiers.map((el, idx) => {
                    const isSelected = el.tier === selectedTier;
                    return (
                        <>
                            {el.tier === subscribedTier ? (
                                <div {...defns.subscribedCard} key={getUniqueKey()}>
                                    <AfitdTypography {...defns.subscribedCardTierTitle} />
                                    <AfitdTypography {...defns.subscribedCardStatusText} />
                                    <AfitdTypography {...defns.subscribedCardTierText} />
                                    {/* <AfitdTypography {...defns.subscribedCardPrice}>
                                        ${numberWithCommas(el.priceMo)}
                                        <AfitdTypography {...defns.subscribedCardPriceSpan}>
                                            {' '}
                                            / <b>USD</b> month
                                        </AfitdTypography>
                                    </AfitdTypography> */}
                                    {/* <AfitdTypography {...defns.subscribedCardPrice}>
                                        ${numberWithCommas(el.priceYr)}
                                        <AfitdTypography {...defns.subscribedCardPriceSpan}>
                                            {' '}
                                            / <b>USD</b> year
                                        </AfitdTypography>
                                    </AfitdTypography> */}
                                    {/* {!trialAvailable && (
                                        <>
                                            <AfitdTypography {...defns.subscribedCardTierUpdateButton} />
                                        </>
                                    )} */}
                                    <AfitdTypography {...defns.subscribedCardTierCancelButton} />
                                </div>
                            ) : (
                                <div {...defns.card} key={getUniqueKey()}>
                                    {(pendingAccountSetup || !trialAvailable) && (
                                        <FormControlLabel
                                            className={isSelected ? classes.activeRadioLabel : classes.radioLabel}
                                            value={el.tier}
                                            label={isSelected ? 'Selected' : 'Select'}
                                            control={<Radio disableRipple checked={isSelected} onChange={handleChange} />}
                                        />
                                    )}
                                    <AfitdTypography {...defns.cardTitle}>{getTierTitle(el.tier)?.slice(0, -9)}</AfitdTypography>
                                    <AfitdTypography {...defns.cardTier}>Tier {el.tier}</AfitdTypography>
                                    {/* <AfitdTypography {...defns.cardPrice}>
                                        ${numberWithCommas(el.priceMo)}
                                        <AfitdTypography {...defns.cardPriceSpan}>
                                            {' '}
                                            / <b>USD</b> month
                                        </AfitdTypography>
                                    </AfitdTypography> */}
                                    {/* <AfitdTypography {...defns.cardPrice}>
                                        ${numberWithCommas(el.priceYr)}
                                        <AfitdTypography {...defns.cardPriceSpan}>
                                            {' '}
                                            / <b>USD</b> year
                                        </AfitdTypography>
                                    </AfitdTypography> */}
                                    {/* <AfitdTypography {...defns.cardPriceSave}>SAVE ${numberWithCommas(el.priceMo * 12 - el.priceYr)} on yearly plan</AfitdTypography> */}
                                </div>
                            )}
                        </>
                    );
                })}
            </div>

            {!!selectedTier ? (
                <>
                    <AfitdTypography {...defns.tierTitle}>Benefits - {getTierTitle(selectedTier)}</AfitdTypography>
                    {selectedTier === 1 && (
                        <div {...defns.tierDetailsContainer}>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Unlimited Candidate Resume Views</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Post 3 ‘Active’ Jobs Simultaneously</AfitdTypography>
                            </div>
                            {/* <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>
                                    <span {...defns.engageText}>Engage</span> with 20 unique candidates for each Job Listing
                                </AfitdTypography>
                            </div> */}
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>1 user seat (account login)</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Basic company Brand Page</AfitdTypography>
                            </div>
                        </div>
                    )}
                    {selectedTier === 2 && (
                        <div {...defns.tierDetailsContainer}>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Unlimited Candidate Resume Views</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Post 7 ‘Active’ Jobs Simultaneously</AfitdTypography>
                            </div>
                            {/* <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>
                                    <span {...defns.engageText}>Engage</span> with 60 unique candidates for each Job Listing
                                </AfitdTypography>
                            </div> */}
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>3 user seat (account login)</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Internal chat with your team. Never miss a beat!</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Full company Brand Page</AfitdTypography>
                            </div>
                        </div>
                    )}
                    {selectedTier === 3 && (
                        <div {...defns.tierDetailsContainer}>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Unlimited Candidate Resume Views</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Post Unlimited Jobs Simultaneously</AfitdTypography>
                            </div>
                            {/* <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>
                                    Unlimited candidate <span {...defns.engageText}>engagement</span> for each Job Listing
                                </AfitdTypography>
                            </div> */}
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>10 user seat (account login)</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Internal chat with your team. Never miss a beat!</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Full Company Branding with consolidated job listings page</AfitdTypography>
                            </div>
                            <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Verified ‘Global’ Account Badge –Exclusive benefit to Tier 3 Plans</AfitdTypography>
                            </div>
                            {/* <div {...defns.tierDetail} key={getUniqueKey()}>
                                <CheckIcon {...defns.tickIcon} />
                                <AfitdTypography {...defns.tierDetailText}>Additional seats can be purchased at $199 per month / per seat</AfitdTypography>
                            </div> */}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div {...defns.subscribedTierContainer}>
                        {/* <AfitdTypography>Your billing period ends on {getFormattedDate(billCycleEndDt, userDateFormat)}</AfitdTypography> */}
                        {/* {!trialAvailable && isSubscribedToTier3 && (
                            <div {...defns.additionalSeatsContainer}>
                                <AfitdTypography {...defns.additionalSeatsText} />
                                <AfitdTypography {...defns.cancelAdditionalSeatsButton} />
                                <AfitdTypography {...defns.additionalSeatsButton} />
                            </div>
                        )} */}
                        {subscribedTier >= 2 && (
                            <AfitdTypography {...defns.seatsToRemove}>
                                You have <AfitdTypography {...defns.vacantSeatsText} /> seats for this plan
                                {additionalSeats ? (
                                    <>
                                        <span {...defns.divider}>&nbsp;&nbsp;|</span>&nbsp;&nbsp;You added <AfitdTypography {...defns.additionalSeatsAddedText} /> seats to your
                                        plan
                                    </>
                                ) : null}
                            </AfitdTypography>
                        )}
                        {unsubscribeDt ? (
                            <AfitdTypography {...defns.cancelText}>
                                Cancellation pending. Your account will be closed on {getFormattedDate(billCycleEndDt, userDateFormat)}
                            </AfitdTypography>
                        ) : (
                            <>
                                {!!tierNew && tierNew !== subscribedTier && (
                                    <AfitdTypography>
                                        Your {tierNew > subscribedTier ? 'upgrade' : 'downgrade'} to Tier {tierNew} will be effective on{' '}
                                        {getFormattedDate(billCycleEndDt, userDateFormat)}
                                    </AfitdTypography>
                                )}
                                {!_isNil(billMonthlyNew) && billMonthlyNew !== subscribedMonthly && (
                                    <AfitdTypography>
                                        Your interval change to {billMonthlyNew ? 'Monthly' : 'Yearly'} will be effective on {getFormattedDate(billCycleEndDt, userDateFormat)}
                                    </AfitdTypography>
                                )}
                            </>
                        )}
                        {subscribedTier !== 3 && (
                            <>
                                <AfitdTypography>You can have {subscribedTierDetails?.activeJobMax} 'Active' job listings simultaneously with this plan</AfitdTypography>
                                {/* <AfitdTypography>
                                    You can <span {...defns.engageText}>engage</span>{' '}
                                    {getTotalEngagementMaxByTiersData({ tier: subscribedTier, billMonthly: subscribedMonthly, tiers })} unique job seekers per billing period
                                </AfitdTypography> */}
                                {/* <AfitdTypography>
                                    You have {getEngagementRemainingCountByEmployerTiersData({ currentEmployer, tiers })} engagements remaining for this billing period
                                </AfitdTypography> */}
                            </>
                        )}
                    </div>

                    {/* {_size(currentPeriodEngagementTotalsGroupedByJobTitle) > 0 && (
                        <AfitdTypography {...defns.activeJobEngagementListTitleText}>New engagements in this billing period:</AfitdTypography>
                    )} */}
                    {/* {_map(currentPeriodEngagementTotalsGroupedByJobTitle, (el, idx) => (
                        <AfitdTypography {...defns.job}>
                            <span {...defns.jobTitle}>{el?.jobTitle}</span> - Engaged {el.total} new job seeker{el.total === 1 ? '' : 's'}
                        </AfitdTypography>
                    ))} */}
                </>
            )}

            <div {...defns.buttonBox}>
                {pendingAccountSetup && <AfitdButton {...defns.backButton} />}
                {!pendingAccountSetup && <AfitdButton {...defns.cancelButton} />}
                <AfitdButton {...defns.saveButton} />
            </div>

            <AfitdConfirmDialog {...defns.cancelModal} />
            <OrderDialog {...defns.orderDialog} />
            <PaymentDialog {...defns.paymentModal} />
            {cancelSubscriptionModalFlag && <CancelSubscriptionDialog {...defns.cancelSubscriptionModal} />}
            {/* <DowngradeDialog {...defns.downgradeModal} /> */}
            <AfitdMessageDialog {...defns.engagementInfoModal} />
            <ConfirmDowngradeDialog {...defns.confirmDowngradeModal} />
            <CancelAdditionalSeatDialog {...defns.cancelAdditionalSeatModal} />
        </div>
    );
};

export default Subscription;
